import { useEffect, useState, useMemo } from 'react';
import {
  ReportsPage,
  ReportsHeader,
  ReportsPageInfos,
  ReportsGeneralInfo,
  ReportsPriceInfo,
  ReportsBody,
} from './ReportsComponents';
import { useStore } from '../../context';
import {
  useStatesWithFilterCounter,
  useReportsGetContainersQuery,
  useConvertCurrencyFcQuery,
} from '../../hooks';
import { filterWithUserId, showToRoles } from '../../utils';
import { CurrencyCode } from '../../types';
import { RoleGuard } from '../../routes';
import { handleExportExcelClick } from './handle-export-excel-click';

export const Reports = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      yardCompanyId: '',
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      number: '',
      type: 'All',
      condition: 'All',
      status: 'All',
      startDate: null,
      endDate: null,
    },
    {},
  );

  const convertCurrency = useConvertCurrencyFcQuery();
  const { data: { data, totalCount, defaultCurrencyCode } = { data: [], totalCount: 0 }, refetch } =
    useReportsGetContainersQuery({
      ...filterWithUserId(filteredStates, user),
    });
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('USD');
  const formattedData = useMemo(() => {
    if (!defaultCurrencyCode) return data;

    const customConvertCurrency = (price: number) =>
      Number(convertCurrency(defaultCurrencyCode, currencyCode, price).toFixed(2));

    return data.map((item) => ({
      ...item,
      allPrice: customConvertCurrency(item.allPrice),
      daysInTerminalPrice: customConvertCurrency(item.daysInTerminalPrice),
      gateInCranePrice: customConvertCurrency(item.gateInCranePrice),
      gateOutCranePrice: customConvertCurrency(item.gateOutCranePrice),
    }));
  }, [currencyCode, defaultCurrencyCode, totalCount]);

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  useEffect(() => {
    if (!defaultCurrencyCode) return;

    setCurrencyCode(defaultCurrencyCode);
  }, [defaultCurrencyCode]);

  const handleShowReportsClick = () => {
    setIsSubmitClicked(true);

    if (!String(states.clientId).length) return;
    if (!String(states.yardId).length && !showToRoles(['YARD_MANAGER', 'YARD_DISPATCHER'], user))
      return;

    refetch();
  };

  const handleCurrencyChange = (e: any) => {
    const newCurrencyCode = e.target.value;

    setCurrencyCode(newCurrencyCode);
  };

  return (
    <RoleGuard roles={'adminRead'}>
      <ReportsPage innerScroll={!data.length}>
        <ReportsHeader
          totalCount={totalCount}
          currencyCode={currencyCode}
          handleExportExcelClick={() =>
            handleExportExcelClick({ states, formattedData, user, currencyCode })
          }
          handleShowReportsClick={handleShowReportsClick}
          handleCurrencyChange={handleCurrencyChange}
        />
        {!!data.length && (
          <ReportsPageInfos>
            <ReportsGeneralInfo user={user} {...states} />
            <ReportsPriceInfo data={formattedData} currencyCode={currencyCode} />
          </ReportsPageInfos>
        )}
        <ReportsBody
          user={user}
          data={formattedData}
          currencyCode={currencyCode}
          isSubmitClicked={isSubmitClicked}
          states={states}
          setStates={setStates}
        />
      </ReportsPage>
    </RoleGuard>
  );
};
